import React, { useEffect, useState } from "react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SlidersHorizontal, ListPlus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Shared per-form config: Active Columns (toggle built-in item columns) + Custom Fields (per doc type).
// columns: [{ key, label }]  — built-in item columns for this form.
export function useItemConfig(formKey, docType, columns) {
  const company = localStorage.getItem("vg_company") || "default";
  const storeKey = `vg_fcols_${formKey}_${company}`;
  const [visible, setVisible] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(storeKey)); if (Array.isArray(s)) return s; } catch (e) {}
    return columns.map((c) => c.key);
  });
  useEffect(() => { localStorage.setItem(storeKey, JSON.stringify(visible)); }, [storeKey, visible]);

  const [fields, setFields] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const loadFields = async () => {
    try {
      const r = await http.get("/masters/custom_fields");
      setFields(r.data.filter((f) => (f.doc_type || "all") === docType || f.doc_type === "all"));
    } catch (e) { toast.warning(requestErrorMessage(e, "load custom fields")); }
  };
  useEffect(() => { loadFields(); }, [docType]);

  const activeFields = fields.filter((f) => f.active !== false);
  const show = (key) => visible.includes(key);
  const toggle = (key) => setVisible((v) => v.includes(key) ? v.filter((x) => x !== key) : [...v, key]);
  const allOn = visible.length >= columns.length;

  const Gear = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="columns" data-testid={`form-columns-gear-${formKey}`}><SlidersHorizontal className="h-4 w-4 mr-1" /> Columns</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-2" onCloseAutoFocus={(e) => e.preventDefault()}>
        <div className="text-xs font-semibold px-1 pb-2">Active Columns</div>
        <label className="flex items-center gap-2 px-1.5 py-1.5 rounded hover:bg-slate-50 cursor-pointer border-b mb-1 font-medium text-sm">
          <input type="checkbox" checked={allOn} onChange={() => setVisible(allOn ? [] : columns.map((c) => c.key))} className="h-4 w-4 accent-brand-600" data-testid={`form-cols-all-${formKey}`} /> Select All
        </label>
        <div className="max-h-60 overflow-y-auto space-y-0.5">
          {columns.map((c) => (
            <label key={c.key} className="flex items-center gap-2 px-1.5 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`form-col-opt-${c.key}`}>
              <input type="checkbox" checked={show(c.key)} onChange={() => toggle(c.key)} className="h-4 w-4 accent-brand-600" /> {c.label}
            </label>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const FieldsButton = (
    <Button size="sm" variant="outline" onClick={() => setModalOpen(true)} data-testid={`custom-fields-btn-${formKey}`}>
      <ListPlus className="h-4 w-4 mr-1" /> Custom Fields
    </Button>
  );

  const Modal = <CustomFieldsModal open={modalOpen} onOpenChange={setModalOpen} docType={docType} onChanged={loadFields} />;

  const customHeaders = () => activeFields.map((f) => (
    <th key={f.id} className="px-2 py-2 font-medium text-xs uppercase tracking-wider text-brand-700" data-testid={`custom-col-${f.name}`}>{f.name}</th>
  ));
  const customCells = (item, patch) => activeFields.map((f) => (
    <td key={f.id} className="px-2 py-2">
      <Input value={(item.custom || {})[f.name] || ""} onChange={(e) => patch({ custom: { ...(item.custom || {}), [f.name]: e.target.value } })}
        className="h-9 w-24 text-xs" data-testid={`custom-input-${f.name}`} />
    </td>
  ));
  const customMobileFields = (item, patch, rowIndex) => activeFields.map((f) => (
    <div key={f.id} className="min-w-0">
      <Label htmlFor={`custom-mobile-${formKey}-${rowIndex}-${f.id}`} className="text-xs text-muted-foreground">{f.name}</Label>
      <Input id={`custom-mobile-${formKey}-${rowIndex}-${f.id}`}
        aria-label={`${f.name} for item ${rowIndex + 1}`}
        value={(item.custom || {})[f.name] || ""}
        onChange={(e) => patch({ custom: { ...(item.custom || {}), [f.name]: e.target.value } })}
        className="mt-1 h-11 w-full text-sm" data-testid={`custom-input-${f.name}`} />
    </div>
  ));

  return { show, Gear, FieldsButton, Modal, activeFields, customHeaders, customCells, customMobileFields, colSpanExtra: activeFields.length };
}

function CustomFieldsModal({ open, onOpenChange, docType, onChanged }) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const r = await http.get("/masters/custom_fields"); setRows(r.data.filter((f) => (f.doc_type || "all") === docType)); }
    catch (e) { toast.warning(requestErrorMessage(e, "load custom fields")); }
  };
  useEffect(() => { if (open) load(); }, [open, docType]);

  const addField = async () => {
    const name = window.prompt("New custom field name (e.g. Model No)");
    if (!name || !name.trim()) return;
    try { await http.post("/masters/custom_fields", { name: name.trim(), doc_type: docType, show_in_pdf: true, show_in_totals: false, active: true }); toast.success("Field added"); load(); onChanged?.(); }
    catch (e) { toast.error(requestErrorMessage(e, "add custom field")); }
  };
  const patch = async (f, upd) => {
    try { await http.put(`/masters/custom_fields/${f.id}`, { ...f, ...upd }); await load(); onChanged?.(); }
    catch (e) { toast.error(requestErrorMessage(e, "update custom field")); }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete "${f.name}"?`)) return;
    try { await http.delete(`/masters/custom_fields/${f.id}`); await load(); onChanged?.(); }
    catch (e) { toast.error(requestErrorMessage(e, "delete custom field")); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Custom Fields · {docType.replace(/_/g, " ")}</DialogTitle></DialogHeader>
        <div className="text-xs text-brand-700 bg-brand-50 border border-brand-200 rounded px-3 py-2">Only active fields appear on the form & PDF for this document type.</div>
        <div className="flex justify-end"><Button size="sm" onClick={addField} data-testid="new-custom-field-btn"><Plus className="h-4 w-4 mr-1" /> New Custom Field</Button></div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Name</th><th className="px-3 py-2 font-medium text-center">Show in PDF</th>
            <th className="px-3 py-2 font-medium text-center">Show in Totals</th><th className="px-3 py-2 font-medium text-center">Active</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id} className="border-t" data-testid={`cf-row-${f.name}`}>
                <td className="px-3 py-2 font-medium">{f.name}</td>
                <td className="px-3 py-2 text-center"><Switch checked={f.show_in_pdf !== false} onCheckedChange={(v) => patch(f, { show_in_pdf: v })} /></td>
                <td className="px-3 py-2 text-center"><Switch checked={!!f.show_in_totals} onCheckedChange={(v) => patch(f, { show_in_totals: v })} /></td>
                <td className="px-3 py-2 text-center"><Switch checked={f.active !== false} onCheckedChange={(v) => patch(f, { active: v })} /></td>
                <td className="px-3 py-2 text-right"><button onClick={() => del(f)} className="p-1.5 hover:bg-rose-50 rounded"><Trash2 className="h-4 w-4 text-rose-500" /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No custom fields yet</td></tr>}
          </tbody>
        </table></div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
